/**
 * Saber se o celular dela está na última versão, e trazer a última versão.
 *
 * O app é um PWA, e um PWA guarda o que já baixou. No iPhone, adicionado à tela
 * inicial, ele guarda com afinco: fechar e abrir não basta, e não existe barra
 * de endereço com botão de recarregar. Some o caminho normal de "atualiza aí" —
 * e um erro consertado ontem continua acontecendo com ela hoje, sem que nem ela
 * nem ninguém tenha como saber que o celular ficou para trás.
 *
 * São três coisas, e as três fazem falta:
 *
 * **Saber.** `VERSAO` é o build que está rodando neste aparelho agora;
 * `versaoNoServidor` é o que está publicado. Comparar as duas responde a
 * pergunta de um jeito que não depende de fé.
 *
 * **Atualizar sozinho.** O service worker novo assume assim que baixa
 * (`skipWaiting` e `clientsClaim` no `vite.config.ts`), e aí `controllerchange`
 * dispara. A página aberta continua com o código velho até recarregar, e é isso
 * que `recarregarQuandoTrocar` faz — uma vez, nunca em laço.
 *
 * **Atualizar na mão**, para quando o automático não alcança: o app que ficou
 * dias fechado, o iPhone que insiste no que já tem.
 */

/**
 * Quando este build saiu do forno — substituído pelo Vite em tempo de build.
 *
 * O `typeof` não é cerimônia: fora de um build do Vite a constante não existe
 * mesmo (nos testes de tela, por exemplo), e sem a guarda o módulo inteiro
 * quebra ao ser importado.
 */
export const VERSAO: string =
  typeof __VERSAO_DA_RUTH__ === 'string' ? __VERSAO_DA_RUTH__ : 'desenvolvimento'

/** O que a resposta do `/versao.json` diz. */
export type Situacao = 'em dia' | 'tem versao nova' | 'nao consegui saber'

/**
 * A versão publicada agora, perguntada ao servidor sem passar pelo cache.
 *
 * `no-store` é o ponto todo: perguntar "qual é a última versão?" para o cache é
 * perguntar para quem só sabe o que já tinha.
 */
export async function versaoNoServidor(): Promise<string | null> {
  try {
    const resposta = await fetch('/versao.json', { cache: 'no-store' })
    if (!resposta.ok) return null
    const corpo = (await resposta.json()) as { versao?: unknown }
    return typeof corpo.versao === 'string' ? corpo.versao : null
  } catch {
    // Sem rede: não dá para saber, e dizer "em dia" seria mentir.
    return null
  }
}

/** Em dia, atrasado, ou não deu para saber — as três respostas honestas. */
export async function conferirAVersao(): Promise<Situacao> {
  const publicada = await versaoNoServidor()
  if (publicada === null) return 'nao consegui saber'
  return publicada === VERSAO ? 'em dia' : 'tem versao nova'
}

/**
 * Busca versão nova, apaga o que está guardado e recarrega.
 *
 * Apagar os caches é o passo que parece exagero e não é: sem ele, no app
 * instalado do iPhone, o toque recarrega exatamente a mesma coisa — e um botão
 * que não faz nada é pior do que não ter botão.
 */
export async function atualizarAgora(): Promise<void> {
  try {
    const registro = await navigator.serviceWorker?.getRegistration()
    await registro?.update()
    // Se um service worker novo está esperando a aba fechar, ele assume agora.
    registro?.waiting?.postMessage({ type: 'SKIP_WAITING' })
  } catch {
    // Sem service worker, ou o navegador recusou: recarregar ainda ajuda.
  }

  try {
    if ('caches' in window) {
      const nomes = await caches.keys()
      await Promise.all(nomes.map((nome) => caches.delete(nome)))
    }
  } catch {
    // Modo privado e afins bloqueiam o cache. Segue para o recarregamento.
  }

  window.location.reload()
}

let jaRecarregou = false

/**
 * Recarrega quando um service worker novo assume o comando.
 *
 * A trava existe porque `controllerchange` pode disparar mais de uma vez, e
 * duas recargas seguidas viram um app que pisca na mão dela.
 */
export function recarregarQuandoTrocar(): void {
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (jaRecarregou) return
    jaRecarregou = true
    window.location.reload()
  })
}
